import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cajaService, inventarioService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useMetodosPago } from '../../hooks/useMetodosPago';
import { btnSecundario, money } from '../../components/ui/estilos';
import { DialogoCajon } from '../cuentas/DialogosCuenta';
import { delta, usePeriodo } from './periodo';
import { Barras, Encabezado, Kpis, Progreso, Tarjeta, VerMas } from './ui';
import { num, useReporte } from './useReporte';
import TurnoPanel from '../turno/TurnoPanel';

const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const TOP = 4;

export default function Resumen() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const { label } = useMetodosPago();
  const p = usePeriodo('semana');
  const { datos, error } = useReporte(cajaService.getReporteCompleto, p.actual, p.previo);
  const [alertas, setAlertas] = useState<any[] | null>(null);
  const veInventario = usuario?.rol === 'admin';
  const [cajon, setCajon] = useState(false);

  // Tiempos de cocina (solo admin: el endpoint es soloAdmin). Sale del menú y se ve aquí.
  const [tiempos, setTiempos] = useState<any>(null);
  const [ini, fin] = p.actual;
  useEffect(() => {
    if (!veInventario) return;
    setTiempos(null);
    cajaService.getTiempos(ini, fin).then(r => setTiempos(r.data.promedios)).catch(() => setTiempos(false));
  }, [veInventario, ini, fin]);

  useEffect(() => {
    if (veInventario) inventarioService.getAlertas().then(r => setAlertas(r.data)).catch(() => setAlertas(null));
  }, [veInventario]);

  const a = datos?.actual, prev = datos?.previo;
  const r = a?.resumen, rp = prev?.resumen;
  const ventas = num(r?.total_ventas);
  const margen = ventas && num(r?.total_costo) ? Math.round(num(r?.utilidad) / ventas * 100) : null;
  const totalMetodos = (a?.por_metodo || []).reduce((s: number, m: any) => s + num(m.total), 0);
  const top = [...(a?.top_productos || [])].filter((x: any) => x.unidades > 0).sort((x: any, y: any) => num(y.total_vendido) - num(x.total_vendido)).slice(0, TOP);
  const porDia = (a?.por_dia || []).map((d: any, i: number, arr: any[]) => {
    const f = new Date(`${String(d.fecha).slice(0, 10)}T12:00:00`);
    return { label: arr.length > 7 ? String(f.getDate()) : DIAS[f.getDay()], valor: num(d.ventas), destacada: i === arr.length - 1 };
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Inicio = turno de caja (arriba) + resumen de ventas del periodo (abajo). */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Inicio</h1>
        <button onClick={() => setCajon(true)} style={btnSecundario}>Abrir cajón</button>
      </div>
      {cajon && <DialogoCajon onCerrar={() => setCajon(false)} />}
      <TurnoPanel />
      <Encabezado titulo="Resumen de ventas" p={p} volver={false} sinPersonalizado />
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      {!datos && !error && <div style={{ color: 'var(--muted)' }}>Cargando…</div>}
      {datos && <>
        <Kpis onClick={to => navigate(to)} items={[
          { label: 'Ventas', valor: money(ventas), delta: delta(ventas, num(rp?.total_ventas), p.periodo), to: '/reportes' },
          { label: 'Ticket promedio', valor: money(num(r?.ticket_promedio)), delta: delta(num(r?.ticket_promedio), num(rp?.ticket_promedio), p.periodo), to: '/reportes/clientes' },
          { label: 'Tickets', valor: String(r?.total_comandas || 0), delta: delta(num(r?.total_comandas), num(rp?.total_comandas), p.periodo), to: '/historial-tickets' },
          { label: 'Utilidad estimada', valor: money(num(r?.utilidad)), delta: { texto: margen === null ? 'Sin costos de receta' : `${margen}% margen`, color: 'var(--muted)' }, to: '/reportes' },
        ]} />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 18, alignItems: 'stretch' }}>
          <Tarjeta titulo="Ventas por día" gap={14} extra={<VerMas to="/reportes" />}>
            <Barras datos={porDia} formato={money} />
          </Tarjeta>
          <Tarjeta titulo="Métodos de pago" extra={<VerMas to="/reportes?t=metodos" />}>
            {!totalMetodos && <div style={{ color: 'var(--muted)' }}>Sin cobros en este periodo.</div>}
            {(a?.por_metodo || []).map((m: any) => (
              <Progreso key={m.metodo} label={label(m.metodo)} valor={money(num(m.total))} pct={num(m.total) / (totalMetodos || 1) * 100} />
            ))}
          </Tarjeta>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 18, alignItems: 'stretch' }}>
          <Tarjeta titulo="Más vendidos" extra={<VerMas to="/reportes?t=productos" />}>
            {!top.length && <div style={{ color: 'var(--muted)' }}>Sin ventas en este periodo.</div>}
            {top.map((t: any, i: number) => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--line-2)' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)', width: 22 }}>{i + 1}</span>
                <span style={{ flex: 1, fontSize: 15, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.producto}</span>
                <span style={{ fontSize: 14, color: 'var(--muted)' }}>{t.unidades} u</span>
                <span style={{ fontSize: 15, fontWeight: 700, width: 80, textAlign: 'right' }}>{money(num(t.total_vendido))}</span>
              </div>
            ))}
          </Tarjeta>
          {veInventario && (
            <Tarjeta titulo="Tiempos de cocina" extra={<VerMas to="/tiempos" />}>
              {tiempos === null && <div style={{ color: 'var(--muted)' }}>Cargando…</div>}
              {tiempos === false && <div style={{ color: 'var(--muted)' }}>No se pudieron cargar los tiempos.</div>}
              {tiempos && !tiempos.total_comandas && <div style={{ color: 'var(--muted)' }}>Sin comandas terminadas en este periodo.</div>}
              {tiempos?.total_comandas > 0 && <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 32, fontWeight: 700 }}>{Math.round(num(tiempos.avg_min_total))} min</span>
                  <span style={{ fontSize: 15, color: 'var(--muted)' }}>promedio del pedido a listo · {tiempos.total_comandas} comandas</span>
                </div>
                {[['Del pedido a cocina', tiempos.avg_min_pedido_a_cocina], ['Preparación', tiempos.avg_min_preparacion]].map(([l, v]) => (
                  <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15 }}><span>{l}</span><b>{Math.round(num(v))} min</b></div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15 }}><span>Más rápida / más lenta</span><b>{tiempos.min_total ?? '—'} / {tiempos.max_total ?? '—'} min</b></div>
              </>}
            </Tarjeta>
          )}
          {veInventario && (
            <Tarjeta titulo="Alertas" extra={<VerMas to="/alertas" />}>
              {alertas === null && <div style={{ color: 'var(--muted)' }}>No se pudieron revisar las existencias.</div>}
              {alertas?.length === 0 && <div style={{ color: 'var(--muted)' }}>Todo el inventario está sobre su mínimo.</div>}
              {alertas?.slice(0, 5).map((x: any) => {
                const agotado = num(x.stock_disponible) <= 0;
                return (
                  <button key={x.id} onClick={() => navigate('/alertas')} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--line)', background: 'var(--bg)', textAlign: 'left', cursor: 'pointer', color: 'var(--ink)' }}>
                    <span aria-hidden style={{ width: 9, height: 9, borderRadius: '50%', flex: 'none', background: agotado ? 'var(--danger)' : 'var(--warn)' }} />
                    <span style={{ flex: 1, fontSize: 15 }}>{agotado ? 'Agotado' : 'Stock bajo'}: {x.nombre} ({+num(x.stock_disponible).toFixed(2)} {x.unidad})</span>
                    <span style={{ fontSize: 14, color: 'var(--muted)' }}>→</span>
                  </button>
                );
              })}
            </Tarjeta>
          )}
        </div>
      </>}
    </div>
  );
}
